/**
 * The loan tape's History tab (DOP-481, restyled DOP-627).
 *
 * One row per run, which is one question/answer pair on the thread. Open
 * shows that run on the Tape tab rather than navigating anywhere: the runs
 * are all in the page's hands already.
 */

import React from "react";
import { formatEpochMs } from "../Monitoring/monitoringFormat";
import {
	type LoanTapeRun,
	outputFilenameOf,
} from "../../services/loanTapeDashboardService";

const STATUS_LABELS: Record<LoanTapeRun["status"], string> = {
	pending: "Queued",
	running: "Running",
	completed: "Done",
	failed: "Failed",
};

const STATUS_CLASSES: Record<LoanTapeRun["status"], string> = {
	pending: "bg-gray-100 text-[#5B6472]",
	running: "bg-[#fffaeb] text-[#b54708]",
	completed: "bg-[#ecfdf3] text-[#067647]",
	failed: "bg-[#fef3f2] text-[#b42318]",
};

const CELL = "px-4 py-2.5 align-middle";
const HEAD = `${CELL} text-xs font-semibold text-[#5B6472]`;

export interface TapeHistoryProps {
	/** Newest first. */
	runs: LoanTapeRun[];
	/** The run the Tape tab is showing. */
	selectedRunId: number | null;
	onOpenRun: (runId: number) => void;
}

const TapeHistory: React.FC<TapeHistoryProps> = ({
	runs,
	selectedRunId,
	onOpenRun,
}) => {
	if (runs.length === 0) {
		return (
			<p className="rounded-xl border border-gray-200 bg-white px-4 py-6 text-center text-sm text-[#5B6472]">
				This loan tape has not run yet.
			</p>
		);
	}

	return (
		<section>
			<p className="mb-3 text-sm text-[#5B6472]">
				Every time this tape was built, newest first.
			</p>
			<div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
				<table className="w-full border-collapse text-[13px]">
					<thead>
						<tr className="border-b border-gray-200 bg-[#fafafa] text-left">
							<th className={HEAD}>When</th>
							<th className={HEAD}>How it went</th>
							<th className={HEAD}>File</th>
							<th className={HEAD}>
								<span className="sr-only">Action</span>
							</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-[#f1f2f4]">
						{runs.map((run) => (
							<tr key={run.id}>
								<td className={`${CELL} whitespace-nowrap text-[#1F2937]`}>
									{formatEpochMs(run.started_at_ms)}
								</td>
								<td className={CELL}>
									<span
										className={`inline-flex h-[22px] items-center rounded-full px-2 text-[11.5px] font-semibold ${STATUS_CLASSES[run.status]}`}
									>
										{STATUS_LABELS[run.status]}
									</span>
								</td>
								<td className={`${CELL} text-[#3f4247]`}>
									{outputFilenameOf(run) ?? "—"}
								</td>
								<td className={`${CELL} text-right`}>
									{run.id === selectedRunId ? (
										<span className="text-[13px] text-[#6B7280]">Showing</span>
									) : (
										<button
											type="button"
											onClick={() => onOpenRun(run.id)}
											className="text-[13px] font-medium text-[#004C4B] hover:underline"
										>
											Open
										</button>
									)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</section>
	);
};

export default TapeHistory;
